import React from 'react';
import { Icon } from '@iconify/react';
import { ResearchSummarySection, ResearchSummaryTab } from '../../utils/researchSummary';
import { Card, CardHeader } from './Atomic';
import { StreamdownRendererSimple } from './StreamdownRenderer';

interface ResearchSummarySectionsProps {
  title: string;
  sections: ResearchSummarySection[];
  tab: ResearchSummaryTab;
}

interface SummaryContentItem {
  label?: string;
  value: string;
}

interface SummaryContentGroup {
  title?: string;
  items: SummaryContentItem[];
}

const TAB_STYLES: Record<ResearchSummaryTab, { border: string; bg: string; iconColor: string }> = {
  person: {
    border: 'border-[#5260FF]/20',
    bg: 'bg-gradient-to-br from-[#121212] to-[#5260FF]/5',
    iconColor: 'text-[#5260FF]',
  },
  company: {
    border: 'border-[#4FCDCF]/20',
    bg: 'bg-gradient-to-br from-[#121212] to-[#4FCDCF]/5',
    iconColor: 'text-[#4FCDCF]',
  },
  signals: {
    border: 'border-purple-500/20',
    bg: 'bg-gradient-to-br from-[#121212] to-purple-500/5',
    iconColor: 'text-purple-400',
  },
  outreach: {
    border: 'border-amber-500/20',
    bg: 'bg-gradient-to-br from-[#121212] to-amber-500/5',
    iconColor: 'text-amber-400',
  },
};

const getSectionIconName = (section: ResearchSummarySection) => {
  const title = section.normalizedTitle;

  if (title.includes('hobbies') || title.includes('interests')) {
    return 'bx:run';
  }
  if (title.includes('psychographic')) {
    return 'bx:brain';
  }
  if (title.includes('professional context')) {
    return 'bx:briefcase-alt-2';
  }
  if (title.includes('background') || title.includes('credentials')) {
    return 'bx:bxs-graduation';
  }
  if (title.includes('company')) {
    return 'bx:buildings';
  }
  if (title.includes('digital footprint')) {
    return 'bx:mobile-alt';
  }
  if (title.includes('recent intelligence')) {
    return 'bx:news';
  }
  if (title.includes('outreach')) {
    return 'bx:phone-call';
  }
  if (title.includes('strategic')) {
    return 'bx:target-lock';
  }

  return 'bx:info-circle';
};

const STANDALONE_BOLD_HEADING_PATTERN = /^(?:#{3,6}[ \t]+)?(?:\*\*|__)(.+?)(?:\*\*|__)\s*:?[ \t]*$/;
const BOLD_FIELD_PATTERN = /^(?:[-+*][ \t]+)?(?:\*\*|__)(.+?)(?:\*\*|__)\s*:?[ \t]*(.+)$/;
const LEADING_LIST_MARKER_PATTERN = /^(?:[-+*]|\d+\.)[ \t]+/;

const parseSummaryContentGroups = (content: string): SummaryContentGroup[] => {
  const groups: SummaryContentGroup[] = [];
  let currentGroup: SummaryContentGroup | null = null;

  const ensureGroup = () => {
    if (currentGroup) return currentGroup;
    currentGroup = { items: [] };
    groups.push(currentGroup);
    return currentGroup;
  };

  for (const rawLine of content.replace(/\r\n/g, '\n').split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const headingMatch = line.match(STANDALONE_BOLD_HEADING_PATTERN);
    if (headingMatch) {
      currentGroup = { title: headingMatch[1].trim(), items: [] };
      groups.push(currentGroup);
      continue;
    }

    const fieldMatch = line.match(BOLD_FIELD_PATTERN);
    if (fieldMatch) {
      ensureGroup().items.push({ label: fieldMatch[1].trim(), value: fieldMatch[2].trim() });
      continue;
    }

    const value = line.replace(LEADING_LIST_MARKER_PATTERN, '').trim();
    if (value) ensureGroup().items.push({ value });
  }

  return groups.filter((group) => group.items.length > 0);
};

const CompactMarkdown: React.FC<{ content: string; featured?: boolean }> = ({ content, featured = false }) => (
  <StreamdownRendererSimple
    content={content}
    className={`${featured ? '[&_.prose]:text-[13px] [&_.prose]:text-gray-200' : '[&_.prose]:text-[12px] [&_.prose]:text-gray-300'} [&_.prose]:leading-5 [&_p]:my-0 [&_ul]:my-0 [&_ol]:my-0 [&_li]:my-0`}
  />
);

const SignalResearchCard: React.FC<{ section: ResearchSummarySection; primary?: boolean }> = ({ section, primary = false }) => {
  const groups = parseSummaryContentGroups(section.content);

  return (
    <Card className="min-w-0 border-cyan-300/10">
      <CardHeader
        title={section.title}
        icon={<Icon icon={getSectionIconName(section)} className="h-5 w-5" aria-hidden="true" />}
        iconColor="text-cyan-300"
      />
      <div className={primary ? 'grid grid-cols-1 gap-3 sm:grid-cols-2' : 'space-y-3'}>
        {groups.map((group, groupIndex) => {
          const featured = primary && Boolean(group.title?.toLowerCase().includes('value proposition'));
          return (
            <section
              key={`${group.title || 'overview'}-${groupIndex}`}
              className={`min-w-0 rounded-xl border border-white/[0.05] bg-black/10 p-3 ${featured ? 'sm:col-span-2' : ''}`}
              aria-label={group.title || `${section.title} details`}
            >
              {group.title && (
                <div className="mb-2 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300/70" aria-hidden="true" />
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.13em] text-cyan-100/75">{group.title}</h4>
                </div>
              )}
              <dl className="divide-y divide-white/[0.05]">
                {group.items.map((item, itemIndex) => (
                  <div key={`${item.label || 'detail'}-${itemIndex}`} className={itemIndex > 0 ? 'mt-2.5 pt-2.5' : ''}>
                    {item.label ? (
                      <>
                        <dt className="mb-0.5 text-[11px] font-semibold text-white/75">{item.label}</dt>
                        <dd><CompactMarkdown content={item.value} /></dd>
                      </>
                    ) : (
                      <dd><CompactMarkdown content={item.value} featured={featured} /></dd>
                    )}
                  </div>
                ))}
              </dl>
            </section>
          );
        })}
      </div>
    </Card>
  );
};

const SignalsResearchSections: React.FC<{ sections: ResearchSummarySection[] }> = ({ sections }) => {
  const strategicSection = sections.find((section) => section.normalizedTitle.includes('strategic intelligence'));
  const supportingSections = strategicSection
    ? sections.filter((section) => section !== strategicSection)
    : sections;

  if (!strategicSection) {
    return (
      <div className={`grid grid-cols-1 gap-4 ${supportingSections.length > 1 ? 'xl:grid-cols-2' : ''}`}>
        {supportingSections.map((section, index) => (
          <SignalResearchCard key={`${section.normalizedTitle}-${index}`} section={section} />
        ))}
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 gap-4 xl:items-start ${supportingSections.length > 0 ? 'xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]' : ''}`}>
      <SignalResearchCard section={strategicSection} primary />
      {supportingSections.length > 0 && (
        <div className="min-w-0 space-y-4">
          {supportingSections.map((section, index) => (
            <SignalResearchCard key={`${section.normalizedTitle}-${index}`} section={section} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ResearchSummarySections: React.FC<ResearchSummarySectionsProps> = ({ title, sections, tab }) => {
  if (!sections.length) return null;

  if (tab === 'signals') return <SignalsResearchSections sections={sections} />;

  const styles = TAB_STYLES[tab];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 px-1">
        <div className={`h-px flex-1 bg-gradient-to-r ${tab === 'person' ? 'from-[#5260FF]/40' : tab === 'company' ? 'from-[#4FCDCF]/40' : 'from-amber-500/40'} to-transparent`} />
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500">{title}</div>
        <div className={`h-px flex-1 bg-gradient-to-l ${tab === 'person' ? 'from-[#5260FF]/40' : tab === 'company' ? 'from-[#4FCDCF]/40' : 'from-amber-500/40'} to-transparent`} />
      </div>

      <div className="grid grid-cols-1 gap-6">
        {sections.map((section, index) => (
          <Card key={`${section.normalizedTitle}-${index}`} className={`${styles.border} ${styles.bg}`}>
            <CardHeader
              title={section.title}
              icon={<Icon icon={getSectionIconName(section)} className="h-5 w-5" aria-hidden="true" />}
              iconColor={styles.iconColor}
            />
            <div className="text-sm text-gray-300">
              <StreamdownRendererSimple content={section.content} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
